import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ChevronLeft, ArrowRight, Loader2, Save, FileText, AlertCircle, CloudOff, Cloud, Info } from 'lucide-react';
import { PraticaService, ClientService, SettingsService, PreventiviService } from "../services/apiService";
import { Client } from "../types";
import { getClientEmail, getClientTelefono, getClientResidenza } from "../utils/clientDisplay";
import { nomeBaseFileMandato, selectMandatoPdf, selectSoggettoPdf } from "../utils/utils";

// Step del wizard
import { Step1_DocumentoCliente } from '../components/Step1_DocumentoCliente';
import { Step2_DatiPratica } from '../components/Step2_DatiPratica';
import { Step3_ContattiDelegati } from '../components/Step3_ContattiDelegati';
import { Step4_SoggettoInvestigato } from '../components/Step4_SoggettoInvestigato';
import { Step_Economico } from '../components/Step_Economico';
import { pdf, PDFDownloadLink } from "@react-pdf/renderer";
import { PDFDocument } from 'pdf-lib';
import { MandatoPDF, MANDATO_FOOTER_PAGES } from "../components/MandatoPDF";
import { BonificaPDF, BONIFICA_FOOTER_PAGES } from "../components/BonificaPDF";
import { ConsulenzaPDF, CONSULENZA_FOOTER_PAGES } from "../components/ConsulenzaPDF";
import { resolvePrivacyBlob, appendPrivacyPolicy, immagineInPng } from "../utils/privacyPdf";
import { useAuth } from "../contexts/AuthContext";
import { DatiSoggettoPDF } from "../components/DatiSoggettoPDF";
import { Modal } from "../components/Modal";
import { useToast } from "../components/ToastContext";

interface BackendFieldDef {
    key: string;
    label: string;
    description: string;
    type: string;
    required: boolean;
}

export const NuovaPraticaView: React.FC = () => {
    const navigate = useNavigate();
    const { id } = useParams();
    const { user, currentSedeId } = useAuth();
    const { addToast } = useToast();

    const topRef = useRef<HTMLDivElement>(null);

    // Disabilita l'autofill del browser su tutti i campi, anche quelli degli step dinamici.
    // Chrome ignora `autoComplete="off"` sul form ma rispetta "new-password" sui singoli input.
    useEffect(() => {
        const root = topRef.current;
        if (!root) return;
        const patch = () => {
            root.querySelectorAll<HTMLElement>('input, select, textarea').forEach(el => {
                const tag = el.tagName.toLowerCase();
                const type = (el as HTMLInputElement).type;
                if (tag === 'input' && (type === 'checkbox' || type === 'radio' || type === 'file' || type === 'submit' || type === 'button')) return;
                el.setAttribute('autocomplete', tag === 'input' ? 'new-password' : 'off');
                el.setAttribute('autocorrect', 'off');
                el.setAttribute('autocapitalize', 'off');
                el.setAttribute('spellcheck', 'false');
                if (!el.getAttribute('data-1p-ignore')) el.setAttribute('data-1p-ignore', 'true');
                if (!el.getAttribute('data-lpignore')) el.setAttribute('data-lpignore', 'true');
            });
        };
        patch();
        const observer = new MutationObserver(patch);
        observer.observe(root, { childList: true, subtree: true });
        return () => observer.disconnect();
    }, []);

    // Salvataggio in bozza e blocco
    const [draftId, setDraftId] = useState<number | null>(id ? Number(id) : null);
    const draftIdRef = useRef<number | null>(id ? Number(id) : null); // Ref per l'autosave senza dipendenze

    const [isAutoSaving, setIsAutoSaving] = useState(false);
    const [lastSaved, setLastSaved] = useState<Date | null>(null);
    const saveTimerRef = useRef<NodeJS.Timeout | null>(null);
    const isDirtyRef = useRef(false);
    const hasLoadedRef = useRef(!id); // true subito per una nuova pratica, false finché loadPratica non termina

    const [isDraftFromDb, setIsDraftFromDb] = useState(false);
    // Stato del mandato all'apertura del wizard, prima di qualunque autosave (che scrive BOZZA):
    // al salvataggio finale un mandato che era "In Corso" torna "In Corso", gli altri "Da Attivare".
    const statoOriginaleRef = useRef<string | null>(null);
    const lockCoreFields = !!id && !isDraftFromDb;

    // Stati base
    const [step, setStep] = useState<1 | 2 | 3 | 4 | 5>(1);
    const [globalSettings, setGlobalSettings] = useState<any>(null); // Settings grezzi
    const [pdfReadySettings, setPdfReadySettings] = useState<any>(null); // Settings con immagini base64 per react-pdf
    const [isLoadingFields, setIsLoadingFields] = useState(false);
    const [isPdfGenerating, setIsPdfGenerating] = useState(false); // Overlay durante la generazione PDF
    const [showFirmaModal, setShowFirmaModal] = useState(false);
    // Secondo passo della firma, aperto solo con "Sì, Salva e Invia". Tiene fuori
    // dal modale principale la scelta FEA (compila il cliente / precompila l'operatore),
    // così il caso più comune (firma di persona) resta semplice.
    const [isSavingAndSigning, setIsSavingAndSigning] = useState(false);

    // Preventivi/proforma pendenti del cliente da collegare a questo mandato,
    // scelti esplicitamente: un collegamento automatico agganciava anche documenti
    // che non c'entravano.
    interface PendingPreventivo {
        id: number;
        numeroProgressivo?: string;
        createdAt?: string;
        dataCreazione?: string;
        totale: number;
        tipoDocumento?: 'PREVENTIVO' | 'PROFORMA';
    }
    const [preventiviPendenti, setPreventiviPendenti] = useState<PendingPreventivo[]>([]);
    const [selectedPreventiviIds, setSelectedPreventiviIds] = useState<number[]>([]);
    const [showPreventiviModal, setShowPreventiviModal] = useState(false);
    const [isLoadingPreventiviPendenti, setIsLoadingPreventiviPendenti] = useState(false);

    const [oldPdfIds, setOldPdfIds] = useState<number[]>([]);

    // Cliente
    const [clientMode, setClientMode] = useState<'existing' | 'new'>('existing');
    const [selectedClientId, setSelectedClientId] = useState<string | number>('');
    const [searchQuery, setSearchQuery] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [clientsList, setClientsList] = useState<Client[]>([]);
    const [clientFiles, setClientFiles] = useState<File[]>([]);
    const [bypassDoc, setBypassDoc] = useState(false);

    const [newClientData, setNewClientData] = useState({
        tipo: 'Privato' as 'Privato' | 'Azienda',
        nome: '', surname: '', luogoNascita: '', dataNascita: '', codiceFiscale: '',
        indirizzo: '', citta: '', cap: '', provincia: '',
        email: '', telefono: '',
        docTipo: 'Carta Identità', docNumero: '', docRilascioDa: '', docDataRilascio: '',
        ragioneSociale: '', partitaIva: '', pec: '', codiceSdi: ''
    });

    const [selectedModelId, setSelectedModelId] = useState<string>('');
    const [selectedAmbito, setSelectedAmbito] = useState<string>('');
    const [selectedMandatoCode, setSelectedMandatoCode] = useState<string>('');
    const [dynamicFields, setDynamicFields] = useState<BackendFieldDef[]>([]);
    const [delegati, setDelegati] = useState<any[]>([]);

    const [formValues, setFormValues] = useState<Record<string, any>>({
        comunicazioni_whatsapp: false,
        tipo_compenso: 'consuntivo',
        // Etichetta libera mostrata in /pratiche come riferimento.
        riferimento: '',
        telefono: '', email: '', indirizzo_residenza: '', citta: '', cap: '', prov: '',
        telefono_1: false, telefono_2: false, telefono_fisso: '', fax: '',

        soggetto_tipo: 'Privato',
        soggetto_nome: '',
        soggetto_nato_a: '',
        soggetto_data_nascita: '',
        soggetto_codice_fiscale: '',
        soggetto_indirizzo: '',
        soggetto_citta: '',
        soggetto_cap: '',
        soggetto_prov: '',
        soggetto_telefono: '',
        soggetto_email: '',
        soggetto_social: '',
        soggetto_domicilio_altrove: '',
        soggetto_luoghi_frequentati: '',
        soggetto_altre_info: '',
        soggetto_mezzi: '',
        soggetto_auto: '',
        soggetto_auto_conoscenti: '',
        soggetto_moto: '',
        soggetto_lavoro: '',
        soggetto_hobby: '',
        soggetto_fisico: '',
        soggetto_gia_pedinato: false,
        soggetto_attento: false,
        soggetto_lavoro_particolare: false,
        soggetto_pericoloso: false,
        soggetto_armi: false,
        soggetto_allegati_nomi: '',

        importo_preventivato: '',
        acconto: '',
        consenso_privacy: false,
        clausole_vessatorie: false,
        interesse_diritto_tutelato: '',
        tariffa_oraria_feriale: '',
        tariffa_oraria_festiva: '',
        tariffa_giornaliera: '',
        accordi_specifici: ''
    });

    const [files, setFiles] = useState<File[]>([]);
    // Allegati già caricati sul mandato (in modifica), mostrati nello step Soggetto per
    // rivederli o rimuoverli. I nuovi upload stanno in `files`; le rimozioni vanno in
    // `removedAttachmentIds` e al salvataggio finiscono in `deletedFiles`.
    const [existingAttachments, setExistingAttachments] = useState<{ id: number; name: string; url?: string; isImage: boolean }[]>([]);
    const [removedAttachmentIds, setRemovedAttachmentIds] = useState<number[]>([]);
// Caricamento in corso dopo il click

    const countPdfPages = async (blob: Blob): Promise<number> => {
        const doc = await PDFDocument.load(await blob.arrayBuffer(), { ignoreEncryption: true });
        return doc.getPageCount();
    };

    const buildFooterPunti = (n: number, prefix: string) =>
        Array.from({ length: n }, (_, i) => ({ key: `#otp_${prefix}${i + 1}#`, label: 'Firma qui', dynamic_position: true }));

    // Stato aggiornato da salvare, tenuto in una ref per non resettare il timer
    const payloadRef = useRef<any>(null);

    const filteredClientsBySede = clientsList.filter(client => {
        if (!currentSedeId || currentSedeId === 'ALL') return false;
        return client.sedeId === currentSedeId;
    });

    const isSedeValid = currentSedeId !== null && currentSedeId !== 'ALL';

    const fetchFields = async (docId: string) => {
        return new Promise<BackendFieldDef[]>(resolve => {
            setTimeout(() => {
                resolve([
                    { key: 'rif_numero_pratica', label: 'Nr. Mandato', description: '', type: 'text', required: true },
                    { key: 'codice_mandato', label: 'Oggetto', description: '', type: 'select-mandato', required: true },
                    { key: 'oggetto_mandato_text', label: 'Testo Oggetto', description: '', type: 'textarea', required: true },
                    { key: 'elementi_fatto', label: 'Elementi Fatto', description: '', type: 'select-elementi-fatto', required: true }
                ]);
            }, 400);
        });
    };

    // Caricamento iniziale
    useEffect(() => {
        ClientService.getAll().then(res => {
            if(Array.isArray(res)) setClientsList(res);
            else if(res.success && Array.isArray(res.data)) setClientsList(res.data);
        });

        SettingsService.getAll().then(async res => {
            if (res.success && res.data && res.data.length > 0) {
                const settings = res.data[0];
                setGlobalSettings(settings);
                setFormValues(prev => ({
                    ...prev,
                    tariffa_oraria_feriale: prev.tariffa_oraria_feriale || settings.hourlyRate || '',
                    tariffa_oraria_festiva: prev.tariffa_oraria_festiva || '',
                    tariffa_giornaliera: prev.tariffa_giornaliera || ''
                }));


                // Il footer del mandato non si carica: il PDF ha già un piè di pagina con il
                // campo firma e uno personalizzato si sovrapporrebbe.
                const [logoB64, headerB64, footerB64, mandatoHeaderB64] = await Promise.all([
                    immagineInPng(settings.logoUrl),
                    immagineInPng(settings.reportHeaderUrl),
                    immagineInPng(settings.reportFooterUrl),
                    immagineInPng(settings.mandatoHeaderUrl),
                ]);

                setPdfReadySettings({
                    ...settings,
                    logoUrl:          logoB64          || settings.logoUrl,
                    reportHeaderUrl:  headerB64        || settings.reportHeaderUrl,
                    reportFooterUrl:  footerB64        || settings.reportFooterUrl,
                    mandatoHeaderUrl: mandatoHeaderB64 || settings.mandatoHeaderUrl,
                    mandatoFooterUrl: '',
                });
            }
        }).catch(err => console.error("Errore caricamento settings", err));
    }, []);

    // Ripristino bozza / pratica
    useEffect(() => {
        if (id) {
            const loadPratica = async () => {
                try {
                    const res = await PraticaService.getById(Number(id));
                    const pratica = res.data || res;

                    let recoveredModelId = pratica.modelId || '';

                    if (pratica.datiAggiuntiviJson) {
                        const parsedData = JSON.parse(pratica.datiAggiuntiviJson);
                        // Il riferimento è una colonna di Pratica, non sta nel JSON: lo mettiamo in
                        // formValues così lo Step2 lo legge come gli altri campi.
                        setFormValues({ ...parsedData, riferimento: pratica.riferimento || '' });

                        if (parsedData.selectedModelId) {
                            recoveredModelId = parsedData.selectedModelId;
                        }
                        if (parsedData.selectedAmbito) {
                            setSelectedAmbito(parsedData.selectedAmbito);
                        }
                    } else if (pratica.riferimento) {
                        setFormValues(prev => ({ ...prev, riferimento: pratica.riferimento }));
                    }

                    if (recoveredModelId) {
                        setSelectedModelId(recoveredModelId);
                    }

                    const fields = await fetchFields(recoveredModelId || 'default');
                    setDynamicFields(fields);

                    setClientMode('existing');
                    if (pratica.clientId) {
                        setSelectedClientId(pratica.clientId);
                    } else if (pratica.client && pratica.client.id) {
                        setSelectedClientId(pratica.client.id);
                    }

                    // Oggetto del mandato: colonna della pratica; per i dati vecchi anche dal JSON.
                    let codiceOggetto = pratica.mandatoCode || '';
                    if (!codiceOggetto && pratica.datiAggiuntiviJson) {
                        try { codiceOggetto = JSON.parse(pratica.datiAggiuntiviJson)?.codice_mandato || ''; } catch { /* JSON non valido */ }
                    }
                    if (codiceOggetto) {
                        setSelectedMandatoCode(codiceOggetto);
                    }

                    if (pratica.delegatiJson) {
                        try {
                            setDelegati(JSON.parse(pratica.delegatiJson));
                        } catch (e) {
                            console.error("Errore parse delegati", e);
                        }
                    } else if (pratica.datiAggiuntiviJson) {
                        const parsed = JSON.parse(pratica.datiAggiuntiviJson);
                        if (parsed.delegati_lista) setDelegati(parsed.delegati_lista);
                    }

                    if (pratica.files) {
                        const generatedPdfs = pratica.files
                            .filter((f: any) => f.name.includes("Mandato_") || f.name.includes("DatiSoggetto_"))
                            .map((f: any) => f.id);
                        setOldPdfIds(generatedPdfs);

                        // Solo gli allegati caricati dal wizard (`allegatoMandato` dal BE): il
                        // mandato contiene anche documentazione ufficio, fotogrammi e relazioni,
                        // che qui non vanno mostrati né rimossi.
                        const material = pratica.files
                            .filter((f: any) => f?.name && f.allegatoMandato === true)
                            .map((f: any) => ({
                                id: f.id,
                                name: f.name,
                                url: f.urlCompressed || f.urlOriginal || undefined,
                                isImage: /\.(png|jpe?g|gif|webp|bmp|tiff?)$/i.test(f.name),
                            }));
                        setExistingAttachments(material);
                        setRemovedAttachmentIds([]);
                    }

                    statoOriginaleRef.current = pratica.stato ?? null;
                    if (pratica.stato === 'BOZZA') {
                        setIsDraftFromDb(true);
                        addToast("Bozza ripristinata. Puoi modificare tutti i campi.", "info");
                    } else {
                        setIsDraftFromDb(false);
                    }

                    // Lascia completare tutti i setState prima di tracciare le modifiche
                    setTimeout(() => { hasLoadedRef.current = true; }, 50);

                } catch (error) {
                    console.error("Errore caricamento pratica", error);
                    addToast("Impossibile caricare i dati della pratica.", "error");
                }
            };
            loadPratica();
        }
    }, [id, addToast]);

    useEffect(() => {
        setTimeout(() => {
            if (topRef.current) {
                topRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }
        }, 100);
    }, [step]);


    // Aggiorna la ref dell'autosave con i dati correnti
    useEffect(() => {
        const clienteSnap = clientMode === 'existing'
            ? clientsList.find(c => String(c.id) === String(selectedClientId)) || null
            : null;
        payloadRef.current = {
            clientMode,
            clientId: clientMode === 'existing' ? selectedClientId : null,
            newClientData: clientMode === 'new' ? { ...newClientData, sedeId: currentSedeId } : null,
            mandatoCode: selectedMandatoCode,
            // Il riferimento va al primo livello del payload (colonna di Pratica, non JSON).
            riferimento: formValues.riferimento || null,
            delegati,
            datiPratica: {
                ...formValues,
                delegati_lista: delegati,
                selectedModelId: selectedModelId,
                selectedAmbito: selectedAmbito,
                _clientSnapshot: clienteSnap ?? undefined,
            },
            modelId: selectedModelId,
            sedeId: currentSedeId,
            stato: 'BOZZA'
        };
        if (hasLoadedRef.current) {
            isDirtyRef.current = true;
        }
    }, [clientMode, selectedClientId, newClientData, currentSedeId, selectedMandatoCode, delegati, formValues, selectedModelId, selectedAmbito, clientsList]);

    // Tiene allineato l'id della bozza nella ref
    useEffect(() => {
        draftIdRef.current = draftId;
    }, [draftId]);


    // Autosave ogni 30 secondi
    useEffect(() => {
        if (step > 1) {
            // La digitazione non resetta il timer
            saveTimerRef.current = setInterval(() => {
                performAutoSave();
            }, 30000);
        }
        return () => {
            if (saveTimerRef.current) clearInterval(saveTimerRef.current);
        };
    }, [step]); // Dipende solo dallo step, così non viene ricreato mentre si scrive.

    const performAutoSave = async () => {
        if (!isDirtyRef.current) return;
        const currentPayload = payloadRef.current;
        if (!currentPayload || !currentPayload.modelId || step === 1) return;

        setIsAutoSaving(true);
        try {
            const formData = new FormData();
            formData.append("data", JSON.stringify(currentPayload));

            let savedId = draftIdRef.current;
            let newClientId: number | string | null = null;

            if (savedId) {
                const res = await PraticaService.updatePraticaMultipart(savedId, formData);
                newClientId = res.data?.clientId || res.clientId;
            } else {
                const res = await PraticaService.createPraticaMultipart(formData);
                savedId = res.data?.praticaId || res.praticaId;
                newClientId = res.data?.clientId || res.clientId;

                if (savedId) {
                    setDraftId(savedId);
                }
            }

            // Se è stato creato un nuovo cliente, il form passa a "cliente esistente" col
            // nuovo id, per non crearne un duplicato al salvataggio successivo.
            if (newClientId && currentPayload.clientMode === 'new') {
                // Aggiunge subito il cliente a clientsList: getPdfData lo cerca lì e senza
                // questo genererebbe Mandato/DatiSoggetto con cliente vuoto.
                const snapshot = { ...(currentPayload.newClientData || newClientData), id: newClientId };
                setClientsList(prev =>
                    prev.some(c => String(c.id) === String(newClientId))
                        ? prev
                        : [...prev, snapshot as any]
                );
                setClientMode('existing');
                setSelectedClientId(newClientId);
            }

            setLastSaved(new Date());
        } catch (error) {
            console.error("Errore autosave:", error);
        } finally {
            setIsAutoSaving(false);
        }
    };


    // Quando lo Step1 salva modifiche a un cliente esistente aggiorniamo la copia in
    // clientsList, da cui getPdfData legge i dati per i PDF. Merge e non sostituzione,
    // per non perdere campi assenti nel DTO di dettaglio.
    const handleClientUpdatedFromWizard = (updatedClient: any) => {
        if (!updatedClient?.id) return;
        setClientsList(prev =>
            prev.map(c =>
                String(c.id) === String(updatedClient.id)
                    ? ({ ...c, ...updatedClient } as any)
                    : c
            )
        );
    };

    const buildPdfPayload = (sourceFormValues: any, sourceClientData: any) => {
        const settingsToUse = pdfReadySettings || globalSettings;
        const nomeAziendaFromSettings = globalSettings?.agencyName || '';

        console.group('%c[buildPdfPayload] DEBUG', 'background:#8b0000;color:#fff;padding:2px 6px;border-radius:3px;font-weight:bold;');
        console.log('🌐 globalSettings:', globalSettings);
        console.log('🌐 globalSettings?.agencyName:', globalSettings?.agencyName);
        console.log('🌐 pdfReadySettings:', pdfReadySettings);
        console.log('🌐 pdfReadySettings?.agencyName:', pdfReadySettings?.agencyName);
        console.log('📦 settings che verrà passato (settingsToUse):', settingsToUse);
        console.log('📦 settingsToUse?.agencyName:', settingsToUse?.agencyName);
        console.log('✅ nome_azienda nel payload:', nomeAziendaFromSettings);
        console.groupEnd();

        return {
            cliente: { ...sourceClientData, perContoDi: sourceClientData.denominazioneTerzi || '' },
            pratica: {
                ...sourceFormValues,
                rif_numero: sourceFormValues.rif_numero_pratica || '',
                data_incarico: sourceFormValues.data_incarico || sourceFormValues.data_compilazione || '',
                oggetto_attivita: sourceFormValues.oggetto_mandato_text || '',
                data_inizio: sourceFormValues.data_compilazione || '',
                luogo_firma: sourceClientData.citta || sourceFormValues.citta_residenza || '',
                ambito: selectedAmbito || (sourceFormValues.soggetto_tipo === 'Azienda' ? 'aziendale' : 'privato'),
            },
            delegati: delegati,
            logoUrl: globalSettings?.logoUrl || '',
            nome_azienda: nomeAziendaFromSettings,
            num_ore: sourceFormValues.num_ore || '',
            rimborso_km: sourceFormValues.rimborso_km || '',
            apparati_localizzazione: sourceFormValues.apparati_localizzazione || '',
            cauzione: Number(sourceFormValues.cauzione) || 0,
            acconto_compenso: Number(sourceFormValues.acconto_compenso || sourceFormValues.acconto) || 0,
            localizzazione_satellitare: !!sourceFormValues.localizzazione_satellitare,
            ripresa: !!sourceFormValues.ripresa,
            testo_altro_apparati: sourceFormValues.testo_altro_apparati || '',
            investigatori_esterni: sourceFormValues.investigatori_esterni || '',
            nomina_responsabile: !!sourceFormValues.nomina_responsabile,
            settings: settingsToUse, // Immagini già convertite in PNG base64
        };
    };

    const getPdfData = () => {
        let clienteCorrente: any;
        if (clientMode === 'new') {
            clienteCorrente = newClientData;
        } else {
            const existing = clientsList.find(c => String(c.id) === String(selectedClientId));
            // Se il cliente è appena stato creato dall'autosave e non è ancora in
            // clientsList, usiamo newClientData; altrimenti il PDF uscirebbe senza cliente.
            const hasNewClientData = newClientData && (newClientData.nome || (newClientData as any).ragioneSociale);
            clienteCorrente = existing || (hasNewClientData ? newClientData : {});
        }
        return buildPdfPayload(formValues, clienteCorrente);
    };

    const handleNext = async () => {
        if (step === 1) {
            // In modifica modello, ambito e cliente sono già fissati: niente validazione
            // né precompilazione, si aggiorna solo l'anagrafica.
            if (!lockCoreFields) {
                if (!isSedeValid) { alert("Seleziona una sede operativa dalla barra laterale per procedere."); return; }
                if (!selectedModelId) { alert("Seleziona un modello di documento."); return; }
                if (selectedModelId === 'mand-investigativo' && !selectedAmbito) { alert("Seleziona l'ambito del mandato investigativo."); return; }

                // Precompila nello Step3 i contatti del soggetto dai campi nativi del cliente.
                // Per l'Azienda sono i contatti aziendali, non del referente; l'utente può
                // comunque sovrascriverli.
                const popolaContattiSoggetto = (c: any) => {
                    if (!c) return;
                    const residenza = getClientResidenza(c);
                    const isAzienda = c.tipo === 'Azienda';
                    setFormValues(prev => ({
                        ...prev,
                        telefono: isAzienda ? (c.telefono || '') : (c.telefono || ''),
                        email: isAzienda ? (c.email || '') : (c.email || ''),
                        pec: c.pec || '',
                        indirizzo_residenza: residenza.indirizzo,
                        citta: residenza.citta,
                        cap: residenza.cap,
                        prov: residenza.provincia,
                    }));
                };

                if (clientMode === 'existing') {
                    if (!selectedClientId) { alert("Seleziona un cliente esistente."); return; }
                    const client = clientsList.find(c => c.id === selectedClientId);
                    popolaContattiSoggetto(client);
                } else {
                    if (newClientData.tipo === 'Privato' && (!newClientData.nome || !newClientData.surname)) { alert("Inserisci Nome e Cognome."); return; }
                    if (newClientData.tipo === 'Azienda' && !newClientData.ragioneSociale) { alert("Inserisci Ragione Sociale."); return; }
                    if (!bypassDoc && clientFiles.length === 0) {
                        alert("È obbligatorio scattare/caricare almeno una foto del documento d'identità.\n\nSe vuoi caricare il documento in un secondo momento, spunta la casella 'Prosegui senza caricare documenti' in fondo alla pagina.");
                        return;
                    }
                    popolaContattiSoggetto(newClientData);
                }
            }

            if (dynamicFields.length === 0 && selectedModelId) {
                setIsLoadingFields(true);
                const fields = await fetchFields(selectedModelId);
                setDynamicFields(fields);
                setIsLoadingFields(false);
            }
            setStep(2);
        } else if (step === 2) {
            if (selectedModelId === 'prev-bonifiche') {
                handleSubmit();
                return;
            }
            setStep(3);
        } else if (step === 3) {
            if (selectedModelId === 'prev-consulenza') {
                handleSubmit();
                return;
            }
            setStep(4);
        } else if (step === 4) {
            setStep(5);
        }
    };

    const handleBack = () => {
        if (step > 1) setStep(prev => (prev - 1) as any);
        else navigate('/pratiche');
    };

    const handleSubmit = async () => {
        // Consulenza e bonifiche non hanno lo step "Soggetto Investigato" (vanno
        // direttamente al submit), quindi niente validazione del nome soggetto.
        const richiedeSoggetto = selectedModelId !== 'prev-bonifiche' && selectedModelId !== 'prev-consulenza';
        if (richiedeSoggetto) {
            const hasSoggetto = formValues.soggetti?.some((s: any) => s.nome?.trim()) || !!formValues.soggetto_nome;
            if (!hasSoggetto) { alert("Inserire almeno il Nome/Denominazione del soggetto investigato."); return; }
        }

        // Per un cliente esistente carichiamo i preventivi/proforma pendenti e, se ce
        // ne sono, mostriamo la selezione prima della firma. Lista vuota o errore:
        // si prosegue normalmente.
        if (clientMode === 'existing' && selectedClientId) {
            setIsLoadingPreventiviPendenti(true);
            try {
                const res = await PreventiviService.getPendingByClient(Number(selectedClientId));
                const pendenti = (res.data || []) as PendingPreventivo[];
                if (pendenti.length > 0) {
                    setPreventiviPendenti(pendenti);
                    setSelectedPreventiviIds([]); // Deselezionati di default
                    setShowPreventiviModal(true);
                    setIsLoadingPreventiviPendenti(false);
                    return; // Si attende la chiusura della modale; "Avanti" apre showFirmaModal
                }
            } catch (err) {
                console.warn('Errore caricamento preventivi pendenti, procedo senza selezione:', err);
                // Un errore di caricamento non blocca il salvataggio.
            } finally {
                setIsLoadingPreventiviPendenti(false);
            }
        }

        setShowFirmaModal(true);
    };

    /**
     * Salva la pratica e, se richiesto, avvia la firma OTP.
     *
     * @param avviaFirma         se true, dopo il salvataggio invia i documenti a OTP Service.
     * @param salvaComeBozza     se true, salva come BOZZA (senza PDF né firma).
     * @param precompilaIdentita se true insieme ad avviaFirma, non invia subito a OTP ma
     * apre il dettaglio pratica con `?firma=precompila` per
     * compilare prima i dati FEA del cliente.
     */
    const eseguiSalvataggio = async (avviaFirma: boolean, salvaComeBozza: boolean = false, precompilaIdentita: boolean = false) => {
        setIsLoadingFields(true);
        if (avviaFirma) setIsSavingAndSigning(true);
        setShowFirmaModal(false);

        try {
            const formData = new FormData();
            const clienteSnap = clientMode === 'existing'
                ? clientsList.find(c => String(c.id) === String(selectedClientId)) || null
                : null;
            const payload = {
                clientMode,
                clientId: clientMode === 'existing' ? selectedClientId : null,
                newClientData: clientMode === 'new' ? { ...newClientData, sedeId: currentSedeId } : null,
                mandatoCode: selectedMandatoCode,
                // Il riferimento va al primo livello del payload.
                riferimento: formValues.riferimento || null,
                delegati,
                datiPratica: {
                    ...formValues,
                    delegati_lista: delegati,
                    selectedModelId: selectedModelId,
                    selectedAmbito: selectedAmbito,
                    _clientSnapshot: clienteSnap ?? undefined,
                },
                modelId: selectedModelId,
                sedeId: currentSedeId,
                stato: salvaComeBozza
                    ? 'BOZZA'
                    : statoOriginaleRef.current === 'In Corso' ? 'In Corso' : 'Da Attivare',
                // Id scelti nella modale di selezione (vuoto per cliente nuovo, nessun
                // pendente o "Salta"). Il backend collega solo questi.
                preventiviDaCollegare: selectedPreventiviIds,
            };

            files.forEach(file => formData.append("files", file));

            // File da eliminare al salvataggio: i PDF da rigenerare (in modifica) e gli
            // allegati rimossi dallo step Soggetto.
            const filesToDelete = [
                ...((draftIdRef.current && oldPdfIds.length > 0) ? oldPdfIds : []),
                ...removedAttachmentIds,
            ];
            if (filesToDelete.length > 0) {
                formData.append("deletedFiles", JSON.stringify(filesToDelete));
            }

            let mandatoPageCount = 0;
            let soggettoPageCount = 0;
            let privacyPageCount = 0;

            if (!salvaComeBozza) {
                const pdfData = getPdfData();
                // Nome del file caricato (finisce nella chiave S3): Nr. Mandato o id, mai il soggetto.
                const safeName = nomeBaseFileMandato(formValues.rif_numero_pratica, draftId);

                clientFiles.forEach(file => {
                    const renamedFile = new File([file], `DocID_${file.name}`, { type: file.type });
                    formData.append("files", renamedFile);
                });

                // Mostriamo l'overlay e lasciamo un frame al browser prima della generazione
                // PDF, che blocca il main thread.
                setIsPdfGenerating(true);
                await new Promise<void>(resolve => setTimeout(resolve, 80));

                try {
                    const settingsForPrivacy = (pdfReadySettings || globalSettings) as any;
                    // Il blob privacy si genera una volta e si riusa per tutti i mandati del salvataggio
                    let privacyCached: { blob: Blob; source: 'AUTO' | 'UPLOAD' } | null = null;
                    try {
                        privacyCached = await resolvePrivacyBlob(settingsForPrivacy);
                        if (privacyCached) {
                            // Pagine della sola privacy, per generare le ancore `#otp_pp{n}#`. OTP le cerca
                            // per chiave testuale nel PDF unito, non per indice di pagina.
                            privacyPageCount = await countPdfPages(privacyCached.blob);
                        }
                    } catch (e) {
                        console.warn('resolve privacy nel salvataggio:', e);
                    }

                    const withPrivacy = async (blob: Blob): Promise<Blob> => {
                        if (!privacyCached) return blob;
                        try { return await appendPrivacyPolicy(blob, privacyCached.blob, privacyCached.source); }
                        catch (e) { console.warn('merge privacy nel salvataggio:', e); return blob; }
                    };

                    if (selectedModelId === 'prev-bonifiche') {
                        const mandatoRawBlob = await pdf(<BonificaPDF data={pdfData} />).toBlob();
                        mandatoPageCount = await countPdfPages(mandatoRawBlob);
                        const blob = await withPrivacy(mandatoRawBlob);
                        formData.append("files", new File([blob], `Mandato_${safeName}.pdf`, { type: 'application/pdf' }));
                    } else if (selectedModelId === 'prev-consulenza') {
                        const mandatoRawBlob = await pdf(<ConsulenzaPDF data={pdfData} />).toBlob();
                        mandatoPageCount = await countPdfPages(mandatoRawBlob);
                        const blob = await withPrivacy(mandatoRawBlob);
                        formData.append("files", new File([blob], `Mandato_${safeName}.pdf`, { type: 'application/pdf' }));
                    } else {
                        const mandatoRawBlob = await pdf(<MandatoPDF data={pdfData} />).toBlob();
                        mandatoPageCount = await countPdfPages(mandatoRawBlob);
                        const blob = await withPrivacy(mandatoRawBlob);
                        formData.append("files", new File([blob], `Mandato_${safeName}.pdf`, { type: 'application/pdf' }));
                        const datiBlob = await pdf(<DatiSoggettoPDF data={pdfData} />).toBlob();
                        soggettoPageCount = await countPdfPages(datiBlob);
                        // L'informativa va solo sul mandato: il file del soggetto si salva così com'è
                        // (solo ancore #otp_s#/#otp_sf#).
                        formData.append("files", new File([datiBlob], `DatiSoggetto_${safeName}.pdf`, { type: 'application/pdf' }));
                    }
                } finally {
                    setIsPdfGenerating(false);
                }
            }

            formData.append("data", JSON.stringify({
                ...payload,
                datiPratica: {
                    ...payload.datiPratica,
                    ...(mandatoPageCount > 0 && { mandatoPageCount }),
                    ...(soggettoPageCount > 0 && { soggettoPageCount }),
                    ...(privacyPageCount > 0 && { privacyPageCount }),
                }
            }));

            let nuovaPraticaId;
            if (draftIdRef.current) {
                await PraticaService.updatePraticaMultipart(draftIdRef.current, formData);
                nuovaPraticaId = draftIdRef.current;
            } else {
                const res = await PraticaService.createPraticaMultipart(formData);
                nuovaPraticaId = res.data?.praticaId || res.praticaId;
            }

            // "Precompila io": non inviamo a OTP qui. La pratica è salvata e si apre il
            // dettaglio con un query param che avvia il caricamento dati cliente e il form
            // FEA; sarà DettaglioPraticaView a chiamare avviaFirmaOTP con prefill_dati_identita=true.
            if (avviaFirma && precompilaIdentita && nuovaPraticaId && !salvaComeBozza) {
                addToast("Pratica salvata. Compila i dati identità per inviare la firma.", "success");
                navigate(`/pratiche/${nuovaPraticaId}?firma=precompila`);
                return;
            }

            if (avviaFirma && nuovaPraticaId && !salvaComeBozza) {
                const praticaRaw = await PraticaService.getById(nuovaPraticaId);
                const praticaAggiornata = (praticaRaw as any).data || praticaRaw;
                // PDF mandato/soggetto più recente non ancora firmato (vedi selectPdfDaFirmare).
                const fileMandato = selectMandatoPdf<any>(praticaAggiornata.files);
                const fileSoggetto = selectSoggettoPdf<any>(praticaAggiornata.files);
                // La privacy è dentro il file del mandato (vedi appendPrivacyPolicy), non esiste
                // un file separato: le ancore `#otp_pp{n}#` si cercano nel PDF unito.

                // Un'ancora privacy per pagina: OTP cerca `#otp_pp{n}#` nel PDF unito e mette
                // una firma in basso a destra su ogni pagina dell'informativa. Vale sia per
                // AUTO (InformativaPrivacyPDF) sia per UPLOAD (pdf-lib in appendPrivacyPolicy).
                const privacyPunti = privacyPageCount > 0 ? buildFooterPunti(privacyPageCount, 'pp') : [];

                if (selectedModelId === 'prev-bonifiche') {
                    if (fileMandato) {
                        await PraticaService.avviaFirmaOTP(nuovaPraticaId, {
                            mandato: {
                                fileId: fileMandato.id,
                                puntiDiFirma: [
                                    { key: '#otp_b1#', label: 'Firma Mandante', dynamic_position: true },
                                    { key: '#otp_b2#', label: 'Approvazione Clausole art. 1341/1342 c.c.', dynamic_position: true },
                                    // Limitiamo alle ancore effettivamente presenti: un overflow di contenuto non
                                    // aggiunge footer, e chiedere ancore inesistenti fa rispondere OTP con 422.
                                    ...buildFooterPunti(Math.min(mandatoPageCount, BONIFICA_FOOTER_PAGES), 'bf'),
                                    ...privacyPunti,
                                ]
                            },
                        });
                        addToast("Pratica salvata E inviata in firma digitale con successo!", "success");
                    } else {
                        addToast("Pratica salvata, ma non è stato possibile avviare la firma (File non trovato).", "error");
                    }
                } else if (selectedModelId === 'prev-consulenza') {
                    if (fileMandato) {
                        await PraticaService.avviaFirmaOTP(nuovaPraticaId, {
                            mandato: {
                                fileId: fileMandato.id,
                                puntiDiFirma: [
                                    { key: '#otp_c1#', label: 'Firma Mandante', dynamic_position: true },
                                    // Come per 'bf': limite alle ancore presenti nel PDF.
                                    ...buildFooterPunti(Math.min(mandatoPageCount, CONSULENZA_FOOTER_PAGES), 'cf'),
                                    ...privacyPunti,
                                ]
                            },
                        });
                        addToast("Pratica salvata E inviata in firma digitale con successo!", "success");
                    } else {
                        addToast("Pratica salvata, ma non è stato possibile avviare la firma (File non trovato).", "error");
                    }
                } else if (fileMandato && fileSoggetto) {
                    // Una firma finale per soggetto (#otp_s{i}#): il PDF ha 3 pagine fisse per
                    // soggetto, quindi soggetti = pagine / 3.
                    const numSoggetti = Math.max(1, Math.round(soggettoPageCount / 3));
                    const puntiFinaliSoggetto = Array.from({ length: numSoggetti }, (_, i) => ({
                        key: `#otp_s${i + 1}#`,
                        label: numSoggetti > 1 ? `Firma Soggetto Investigato ${i + 1}` : 'Firma Soggetto Investigato',
                        dynamic_position: true,
                    }));
                    await PraticaService.avviaFirmaOTP(nuovaPraticaId, {
                        mandato: {
                            fileId: fileMandato.id,
                            puntiDiFirma: [
                                { key: '#otp_m1#', label: 'Firma Mandante', dynamic_position: true },
                                { key: '#otp_m2#', label: 'Approvazione Clausole art. 1341/1342 c.c.', dynamic_position: true },
                                { key: '#otp_m3#', label: 'Dichiarazione Finale', dynamic_position: true },
                                // Come per 'bf': limite alle ancore presenti nel PDF.
                                ...buildFooterPunti(Math.min(mandatoPageCount, MANDATO_FOOTER_PAGES), 'mf'),
                                ...privacyPunti,
                            ]
                        },
                        datiSoggetto: {
                            fileId: fileSoggetto.id,
                            puntiDiFirma: [
                                ...puntiFinaliSoggetto,
                                ...buildFooterPunti(soggettoPageCount, 'sf'),
                                // Nessuna ancora privacy sul Dati Soggetto: l'informativa è solo sul mandato.
                            ]
                        },
                    });
                    addToast("Pratica salvata E inviata in firma digitale con successo!", "success");
                } else {
                    addToast("Pratica salvata, ma non è stato possibile avviare la firma (File non trovati).", "error");
                }
            } else {
                addToast(salvaComeBozza ? "Bozza salvata e messa in pausa." : "Pratica salvata con successo!", "success");
            }
            // Dopo il salvataggio apre direttamente il mandato (/pratiche/{id}) invece della lista.
            navigate(nuovaPraticaId ? `/pratiche/${nuovaPraticaId}` : '/pratiche');
        } catch(e: any) {
            console.error("ERRORE DURANTE IL SALVATAGGIO:", e);
            alert(`Errore: ${e.message || "Errore sconosciuto"}`);
        } finally {
            setIsLoadingFields(false);
            setIsSavingAndSigning(false);
        }
    };

    return (
        <div className="flex flex-col min-h-screen bg-gray-50/50" ref={topRef}>

            {/* Overlay generazione PDF */}
            {isPdfGenerating && (
                <div className="fixed inset-0 z-[9999] flex flex-col items-center justify-center bg-black/60 backdrop-blur-sm">
                    <div className="bg-white rounded-2xl shadow-2xl px-10 py-8 flex flex-col items-center gap-4 min-w-[260px]">
                        <svg className="animate-spin h-10 w-10 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"/>
                        </svg>
                        <p className="text-gray-800 font-semibold text-base">Generazione PDF in corso…</p>
                        <p className="text-gray-400 text-sm text-center">
                            {selectedModelId === 'prev-bonifiche' ? 'Incarico bonifica' : selectedModelId === 'prev-consulenza' ? 'Incarico consulenza' : 'Mandato e dati soggetto'}
                        </p>
                    </div>
                </div>
            )}

            {/* Main Content Container */}
            <div className="p-4 sm:p-6 max-w-5xl mx-auto w-full flex-1">

                {/* Header con stato bozza */}
                <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 mb-6">
                    <div className="flex items-center gap-2 cursor-pointer text-gray-500 hover:text-gray-900 w-fit" onClick={handleBack}>
                        <ChevronLeft size={20}/> Indietro
                    </div>

                    <div className="flex flex-wrap items-center gap-2">
                        {isAutoSaving ? (
                            <div className="flex items-center gap-1.5 text-xs font-semibold text-blue-500 bg-blue-50 px-3 py-1.5 rounded-full border border-blue-100 shadow-sm animate-pulse">
                                <Loader2 size={12} className="animate-spin"/> Salvataggio in corso...
                            </div>
                        ) : lastSaved ? (
                            <div className="flex items-center gap-1.5 text-[10px] font-semibold text-emerald-600 bg-emerald-50 px-3 py-1.5 rounded-full border border-emerald-100 shadow-sm">
                                <Cloud size={14}/> Bozza salvata alle {lastSaved.toLocaleTimeString('it-IT')}
                            </div>
                        ) : (
                            <div className="flex items-center gap-1.5 text-[10px] font-semibold text-slate-400 bg-slate-50 px-3 py-1.5 rounded-full border border-slate-100">
                                <CloudOff size={14}/> Nessun salvataggio recente
                            </div>
                        )}

                        <button
                            onClick={() => eseguiSalvataggio(false, true)}
                            className="text-[10px] font-bold text-slate-600 bg-white border border-slate-200 shadow-sm hover:bg-slate-100 hover:text-blue-600 px-3 py-1.5 rounded-lg transition-colors ml-2"
                            title="Metti in pausa e riprendi dopo da 'Pratiche'"
                        >
                            Pausa / Salva ed esci
                        </button>
                    </div>
                </div>

                <div className="flex justify-between items-end mb-6">
                    <div>
                        <h1 className="text-2xl font-bold text-gray-900 mb-1">
                            {step === 1 && "Documento & Cliente"}
                            {step === 2 && "Dati Pratica & Mandato"}
                            {step === 3 && "Contatti & Deleghe"}
                            {step === 4 && "Condizioni Economiche"}
                            {step === 5 && "Soggetto Investigato"}
                        </h1>
                        <p className="text-sm text-gray-500">
                            Step {step} di {selectedModelId === 'prev-bonifiche' ? 2 : selectedModelId === 'prev-consulenza' ? 3 : 5}
                            {draftId && (isDraftFromDb || !id) && <span className="ml-2 font-bold text-amber-500">[Modalità Bozza]</span>}
                        </p>
                    </div>
                    <div className="flex gap-1">
                        {(selectedModelId === 'prev-bonifiche' ? [1, 2] : selectedModelId === 'prev-consulenza' ? [1, 2, 3] : [1, 2, 3, 4, 5]).map(s => (
                            <div key={s} className={`h-2 w-8 rounded-full ${s <= step ? 'bg-blue-600' : 'bg-gray-200'}`}/>
                        ))}
                    </div>
                </div>

                {/* Step */}
                {step === 1 && (
                    <div className="space-y-4">
                        {lockCoreFields && (
                            <div className="bg-blue-50 border border-blue-200 p-4 rounded-xl flex items-start gap-3 text-blue-800 animate-in fade-in">
                                <Info className="shrink-0 mt-0.5" size={20} />
                                <div>
                                    <p className="font-bold">Modalità Modifica Attiva</p>
                                    <p className="text-sm">Il modello di documento e il cliente selezionato <strong>non possono essere cambiati</strong> in una pratica già in corso. Puoi continuare ad aggiornare solo i dati anagrafici e operativi.</p>
                                </div>
                            </div>
                        )}

                        {!isSedeValid && (
                            <div className="bg-amber-50 border border-amber-200 p-4 rounded-xl flex items-start gap-3 text-amber-800 animate-pulse">
                                <AlertCircle className="shrink-0" size={20} />
                                <div>
                                    <p className="font-bold">Sede non selezionata</p>
                                    <p className="text-sm">Per creare un mandato è necessario selezionare una sede specifica dal menu laterale.</p>
                                </div>
                            </div>
                        )}
                        <Step1_DocumentoCliente
                            selectedModelId={selectedModelId} setSelectedModelId={setSelectedModelId}
                            selectedAmbito={selectedAmbito} setSelectedAmbito={setSelectedAmbito}
                            clientMode={clientMode} setClientMode={setClientMode}
                            selectedClientId={selectedClientId} setSelectedClientId={setSelectedClientId}
                            clientsList={filteredClientsBySede}
                            searchQuery={searchQuery} setSearchQuery={setSearchQuery}
                            newClientData={newClientData} setNewClientData={setNewClientData}
                            isLoadingClients={false} currentPage={currentPage} setCurrentPage={setCurrentPage}
                            disabled={!isSedeValid}
                            clientFiles={clientFiles} setClientFiles={setClientFiles}
                            bypassDoc={bypassDoc} setBypassDoc={setBypassDoc}
                            lockCoreFields={lockCoreFields}
                            onClientUpdated={handleClientUpdatedFromWizard}
                        />
                    </div>
                )}

                {step === 2 && (
                    <Step2_DatiPratica
                        dynamicFields={dynamicFields}
                        formValues={formValues}
                        setFormValues={setFormValues}
                        selectedMandatoCode={selectedMandatoCode}
                        setSelectedMandatoCode={setSelectedMandatoCode}
                        selectedModelId={selectedModelId}
                        selectedAmbito={selectedAmbito}
                        lockCoreFields={lockCoreFields}
                        // In modifica il titolare può cambiare la tipologia del mandato; cliente e
                        // modello restano bloccati da `lockCoreFields`. La sola lettura nello storico
                        // arriva da StoricoPraticaView, che non passa `lockMandatoType`.
                        lockMandatoType={false}
                    />
                )}
                {step === 3 && selectedModelId !== 'prev-bonifiche' && <Step3_ContattiDelegati formValues={formValues} setFormValues={setFormValues} delegati={delegati} setDelegati={setDelegati} selectedModelId={selectedModelId} />}
                {step === 4 && <Step_Economico formValues={formValues} setFormValues={setFormValues} />}
                {step === 5 && <Step4_SoggettoInvestigato formValues={formValues} setFormValues={setFormValues} files={files} setFiles={setFiles}
                    existingAttachments={existingAttachments.filter(a => !removedAttachmentIds.includes(a.id))}
                    onRemoveExisting={(fid: number) => setRemovedAttachmentIds(prev => prev.includes(fid) ? prev : [...prev, fid])} />}

            </div> {/* Chiusura Main Content Container */}

            {/* Footer fisso */}
            <div className="sticky bottom-0 w-full bg-white/90 backdrop-blur-md border-t border-gray-200 p-4 sm:px-8 flex justify-between items-center z-50 shadow-[0_-10px_20px_-5px_rgba(0,0,0,0.05)]">
                <div className="text-sm text-gray-500 font-medium hidden sm:block">
                    Step {step}/{selectedModelId === 'prev-bonifiche' ? 2 : selectedModelId === 'prev-consulenza' ? 3 : 5}
                </div>
                <div className="flex w-full sm:w-auto justify-between sm:justify-end gap-3 sm:gap-4">
                    <button onClick={handleBack} className="px-5 py-2.5 text-gray-600 hover:bg-gray-100 rounded-lg font-medium transition-colors">
                        {step === 1 ? "Annulla" : "Indietro"}
                    </button>

                    {(step < 5 && !(selectedModelId === 'prev-bonifiche' && step === 2) && !(selectedModelId === 'prev-consulenza' && step === 3)) ? (
                        <button onClick={handleNext} disabled={isLoadingFields} className="bg-blue-600 text-white px-8 py-2.5 rounded-lg flex items-center justify-center gap-2 hover:bg-blue-700 font-medium shadow-lg transition-all active:scale-[0.98]">
                            {isLoadingFields ? <Loader2 className="animate-spin" size={20}/> : <ArrowRight size={20}/>} Avanti
                        </button>
                    ) : (
                        <div className="flex flex-wrap items-center gap-3">

                            <button onClick={handleSubmit} disabled={isLoadingFields} className="bg-green-600 text-white px-8 py-2.5 rounded-lg flex items-center gap-2 hover:bg-green-700 font-bold shadow-lg transition-all">
                                {isLoadingFields ? <Loader2 className="animate-spin" size={20}/> : <Save size={20}/>} {draftId ? 'Conferma e Salva' : 'Salva Pratica'}
                            </button>
                        </div>
                    )}
                </div>
            </div>

            {/* Modale firma OTP */}
            {/*
              Selezione dei preventivi/proforma pendenti del cliente da collegare al
              mandato, nuovo o in modifica. Tutti deselezionati di default.
            */}
            {showPreventiviModal && (
                <Modal
                    title="Collega preventivi o proforma esistenti?"
                    onClose={() => setShowPreventiviModal(false)}
                >
                    <div className="space-y-4">
                        <p className="text-sm text-gray-600">
                            Questo cliente ha <strong>{preventiviPendenti.length}</strong>{' '}
                            {preventiviPendenti.length === 1 ? 'documento non ancora collegato' : 'documenti non ancora collegati'} a nessun mandato.
                            Seleziona quelli che vuoi agganciare a questo {id ? '' : 'nuovo '}mandato. Quelli non selezionati
                            restano disponibili per altri mandati o come fatturazioni indipendenti.
                        </p>

                        <div className="max-h-72 overflow-y-auto border border-slate-200 rounded-lg divide-y divide-slate-100">
                            {preventiviPendenti.map(p => {
                                const isSelected = selectedPreventiviIds.includes(p.id);
                                const dataLabel = p.dataCreazione || p.createdAt;
                                const dataFmt = dataLabel ? new Date(dataLabel).toLocaleDateString('it-IT') : '—';
                                return (
                                    <label
                                        key={p.id}
                                        className={`flex items-center gap-3 p-3 cursor-pointer hover:bg-slate-50 transition-colors ${
                                            isSelected ? 'bg-blue-50' : 'bg-white'
                                        }`}
                                    >
                                        <input
                                            type="checkbox"
                                            checked={isSelected}
                                            onChange={e => {
                                                setSelectedPreventiviIds(prev =>
                                                    e.target.checked
                                                        ? [...prev, p.id]
                                                        : prev.filter(id => id !== p.id)
                                                );
                                            }}
                                            className="w-4 h-4 accent-blue-600 shrink-0"
                                        />
                                        <div className="flex-1 min-w-0">
                                            <span className="text-sm font-bold text-slate-900">
                                                {p.numeroProgressivo || `#${p.id}`}
                                            </span>
                                            <div className="text-xs text-slate-500 mt-0.5">
                                                {dataFmt}
                                            </div>
                                        </div>
                                        <div className="text-sm font-bold text-slate-700 shrink-0 tabular-nums">
                                            € {Number(p.totale || 0).toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                        </div>
                                    </label>
                                );
                            })}
                        </div>

                        <div className="flex gap-3 pt-2">
                            <button
                                onClick={() => {
                                    setSelectedPreventiviIds([]);
                                    setShowPreventiviModal(false);
                                    setShowFirmaModal(true);
                                }}
                                className="flex-1 bg-white border border-gray-300 text-gray-700 py-2.5 rounded-lg font-medium hover:bg-gray-50 transition-colors"
                            >
                                Salta (nessun collegamento)
                            </button>
                            <button
                                onClick={() => {
                                    setShowPreventiviModal(false);
                                    setShowFirmaModal(true);
                                }}
                                className="flex-1 bg-blue-600 text-white py-2.5 rounded-lg font-medium hover:bg-blue-700 transition-colors"
                            >
                                Avanti{selectedPreventiviIds.length > 0 ? ` (${selectedPreventiviIds.length} selezionat${selectedPreventiviIds.length === 1 ? 'o' : 'i'})` : ''}
                            </button>
                        </div>
                    </div>
                </Modal>
            )}

            {showFirmaModal && (
                <Modal title="Firma Elettronica Avanzata" onClose={() => setShowFirmaModal(false)}>
                    <div className="space-y-4">
                        <p className="text-gray-600">
                            Vuoi inviare immediatamente i documenti al cliente tramite <strong>OTP Service</strong> per la firma digitale?
                        </p>
                        <div className="bg-blue-50 text-blue-800 p-3 rounded-lg text-sm border border-blue-100">
                            Il cliente riceverà un link via email/SMS per validare il proprio documento d'identità e apporre le firme.
                        </div>
                        <div className="flex gap-3 pt-4">
                            <button
                                onClick={() => eseguiSalvataggio(false)}
                                disabled={isSavingAndSigning}
                                className="flex-1 bg-white border border-gray-300 text-gray-700 py-2.5 rounded-lg font-medium hover:bg-gray-50 transition-colors disabled:opacity-50"
                            >
                                Salva, e firma di persona
                            </button>
                            <button
                                onClick={() => { setShowFirmaModal(false); eseguiSalvataggio(true, false, true); }}
                                disabled={isSavingAndSigning}
                                className="flex-1 bg-blue-600 text-white py-2.5 rounded-lg font-medium hover:bg-blue-700 transition-colors flex justify-center items-center gap-2 disabled:opacity-50"
                            >
                                {isSavingAndSigning ? <Loader2 className="animate-spin" size={18}/> : null} Salva, e firma digitale
                            </button>
                        </div>
                    </div>
                </Modal>
            )}
        </div>
    );
};

export default NuovaPraticaView;